var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { HawkinsPropsOverrideContext, isNil, useClasses, useLayoutProps, useProps, } from '@hawkins/helpers';
import { Heading } from '../heading';
import { AppLogo } from '../navigation';
import { Text } from '../text';
import NotFoundIllustration from './illustrations/notFoundIllustration';
import RestrictedIllustration from './illustrations/restrictedIllustration';
import SomethingWrongIllustration from './illustrations/somethingWrongIllustration';
import UnavailableIllustration from './illustrations/unavailableIllustration';
import { useErrorStateStyles } from './errorState.styles';
import { ErrorStateTestIds } from './errorState.testids';
var displayName = 'ErrorState';
var variantIllustrationMap = {
    'not-found': NotFoundIllustration,
    restricted: RestrictedIllustration,
    'something-wrong': SomethingWrongIllustration,
    unavailable: UnavailableIllustration,
};
/**
 * The Error State component provides a consistent way to show when a page or section can’t be loaded.
 */
export var ErrorState = React.forwardRef(function (props, ref) {
    var _a;
    // Props
    var _b = useProps(displayName, props), actions = _b.actions, appLogo = _b.appLogo, classesProp = _b.classes, className = _b.className, _c = _b.component, Component = _c === void 0 ? 'div' : _c, illustration = _b.illustration, layout = _b.layout, _d = _b.size, size = _d === void 0 ? 'large' : _d, styleProp = _b.style, subtitle = _b.subtitle, subtitleProps = _b.subtitleProps, title = _b.title, titleProps = _b.titleProps, _e = _b.variant, variant = _e === void 0 ? 'something-wrong' : _e, restProps = __rest(_b, ["actions", "appLogo", "classes", "className", "component", "illustration", "layout", "size", "style", "subtitle", "subtitleProps", "title", "titleProps", "variant"]);
    var VariantIllustration = variantIllustrationMap[variant];
    var isLarge = size === 'large';
    var isMedium = size === 'medium';
    var isSmall = size === 'small';
    var overrideProps = React.useMemo(function () { return ({
        ButtonGroup: {
            gap: 'space1',
            justifyContent: isSmall ? 'center' : undefined,
        },
    }); }, [isSmall]);
    // Styles
    var baseClasses = useErrorStateStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root, (_a = {},
        _a[classes.rootLarge] = isLarge,
        _a[classes.rootMedium] = isMedium,
        _a[classes.rootSmall] = isSmall,
        _a));
    return (_jsxs(Component, __assign({ className: rootClassName, ref: ref, style: style }, restProps, { children: [_jsxs("div", __assign({ className: classes.contentContainer }, { children: [isLarge && !isNil(appLogo) && _jsx("div", __assign({ className: classes.appLogoContainer }, { children: appLogo !== null && appLogo !== void 0 ? appLogo : _jsx(AppLogo, {}) })), _jsx(Heading, __assign({ className: classes.title, "data-testid": ErrorStateTestIds.TITLE, level: isLarge ? 5 : 3 }, titleProps, { children: title })), !isNil(subtitle) && (_jsx(Text, __assign({ className: classes.subtitle, "data-testid": ErrorStateTestIds.SUBTITLE, level: isLarge ? 2 : 0 }, subtitleProps, { children: subtitle }))), !isNil(actions) && (_jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: overrideProps }, { children: actions })))] })), _jsx("div", __assign({ className: classes.illustrationContainer }, { children: illustration !== null && illustration !== void 0 ? illustration : _jsx(VariantIllustration, { className: classes.illustration }) }))] })));
});
ErrorState.displayName = displayName;
